import BaseLayer from 'ol/layer/Base';
import {Component, inject} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BaseUILayer} from '../../../classes/layers/base-ui-layer';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {MapService} from '../../../../services/map.service';
import {Feature} from 'ol';
import {Geometry} from 'ol/geom';
import {FeatureLike} from 'ol/Feature';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import {LayerService} from '../../../../services/layer.service';
import {map, take} from 'rxjs';
import GeoJSON from 'ol/format/GeoJSON';
import {Circle} from 'ol/style';
import {LayerHeaderComponent} from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-va-data-center-layer',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    LayerHeaderComponent,
  ],
  templateUrl: './va-data-center-layer.component.html',
  standalone: true,
  styleUrl: './va-data-center-layer.component.scss',
})
export class VaDataCenterLayerComponent extends BaseUILayer<BaseLayer> {

  private layerService = inject(LayerService);

  constructor() {
    super();
  }

  override toggleLayer(checked: boolean): void {

    this.checked = checked;

    // If the toggle is turned off, remove the layer and exit
    if (!this.checked) {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
      return;
    }

    // If this is not the first time the layer has been turned on, update the filters and return
    if (!this.layerService.styles.get(this.mapLayer().uiHandle)) {
      const styleLogic = (feature: FeatureLike) => {
        const properties = feature.getProperties() as Record<string, unknown>;

        const color = (dataCenter: Record<string, unknown>) => {
          if (dataCenter['Build_Status'] === 'Proposed') return '#b30000';
          if (dataCenter['Build_Status'] === 'Existing') return '#ebdc78';
          return '#000000';
        };

        return new Style({
          image: new Circle({
            fill: new Fill({
              color: color(properties),
            }),
            stroke: new Stroke({
              color: 'black',
              width: 2,
            }),
            radius: 5,
          }),
        });
      };
      this.layerService.styles.set(this.mapLayer().uiHandle, styleLogic);
    }

    this.getLayerData();

  }

  override onDestroy(): void {
  }

  private getLayerData() {
    const url = `${this.mapLayer().serviceUrl}/${this.mapLayer().serviceLayer}/query`;
    const params = {
      where: '1=1',
      f: 'pgeojson',
      outfields: '*',
      outSR: 3857,
    };
    this.layerService.getJsonData<{features: {properties: Record<string, unknown>}[]}>(url, params).pipe(
      take(1),
      map((featureCollection) => {
        featureCollection.features = featureCollection.features.map((f) => {
          f.properties['popoverData'] = this.mapLayer().createPopover(f.properties);
          return f;
        });
        return featureCollection;
      }),
    ).subscribe(fc => {
      const features = new GeoJSON().readFeatures(fc);
      const source = new VectorSource({
        features: features as Feature<Geometry>[],
        attributions: this.mapLayer().buildAttribution(),
      });



      // If the layer has already been created, just update the drawn features
      if (this.mapLayer().renderedLayer && this.mapService.isLayerOnMap(this.mapLayer().renderedLayer)) {
        (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setSource(source);
        return;
      }

      // Create the initial layer
      this.mapLayer().renderedLayer = new VectorLayer({
        source,
        properties: {
          name: this.mapLayer().displayName,
        },
        opacity: 0.8,
        style: this.layerService.styles.get(this.mapLayer().uiHandle),
      });

      MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
    });
  }

  //TODO:  Move to header component
  override changeOpacity(opacity: number) {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setOpacity(this.opacity / 100);
  }
}
